// src/pages/QuotationRecord/components/QuotationRecordFilters.js
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { setFilters, resetData } from "../QuotationRecordSlice";
import { fetchQuotationRecord } from "../QuotationRecordThunk";
import { currentDateStartTime, currentDateEndTime } from "../../../lib/functions";
import { useDefaultDateRange } from "../../../hooks/useDefaultDateRange";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import RecordDateFilter from "../../../components/filters/RecordDateFilter";

import {
  fetchCustomers,
  fetchEmployees,
  fetchItems,
  fetchGroups,
  fetchCategories,
  fetchLocations,
  fetchUsers,
  fetchTagGroups,
  fetchTagGroupFields,
} from "../../../store/commonThunks/lookups";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";

const QuotationRecordFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.quotationRecord);
  const lookups = useSelector((state) => state.lookups);

  const dynamicSpan = filters.filterType === "By Tag" ? 3 : 4;

  // ✅ set default range on load
  useDefaultDateRange(setFilters, filters, "thisMonth");

  // ✅ fetch lookups
  useEffect(() => {
    dispatch(fetchCustomers());
    // dispatch(fetchEmployees());
    dispatch(fetchItems());
    dispatch(fetchGroups());
    dispatch(fetchCategories());
    dispatch(fetchLocations());
    dispatch(fetchUsers());
    dispatch(fetchTagGroups());
  }, [dispatch]);

  // ✅ auto run report once defaults are ready
  useEffect(() => {
    if (filters.filterType && filters.recordType && filters.fromDate && filters.toDate) {
      handleReport();
    }
  }, [filters]);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.filterType) {
      messageApi.warning("Select Filter Type");
      return;
    }

    dispatch(resetData());

    const payload = {
      selectedFilterType: filters.filterType,
      selectedRecordType: filters.recordType,
      filters: {
        ...filters,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      },
    };

    dispatch(fetchQuotationRecord(payload)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch records ❌");
      }
    });
  };

  const renderExtraField = () => {
    switch (filters.filterType) {
      case "By Customer":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Customer</label>
            <SearchListAutoComplete
              entityType="customer"
              optionsList={lookups.customers.map((c) => ({
                sale_id: c.acc_id,
                display_text: c.acc_name,
              }))}
              onSelect={(obj) => handleFilterChange("customerId", obj?.sale_id)}
              placeholder="Customer"
              
            />
          </Col>
        );
      case "By Item":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Item</label>
            <SearchListAutoComplete
              entityType="item"
              optionsList={lookups.items.map((i) => ({
                sale_id: i.item_id,
                display_text: i.item_name,
              }))}
              onSelect={(obj) => handleFilterChange("itemId", obj?.sale_id)}
              placeholder="Item"
              
            />
          </Col>
        );
      case "By Group":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Group</label>
            <SearchListAutoComplete
              entityType="group"
              optionsList={lookups.groups.map((g) => ({
                sale_id: g.group_id,
                display_text: g.group_name,
              }))}
              onSelect={(obj) => handleFilterChange("groupId", obj?.sale_id)}
              placeholder="Group"
              
            />
          </Col>
        );
      case "By Category":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Category</label>
            <SearchListAutoComplete
              entityType="category"
              optionsList={lookups.categories.map((c) => ({
                sale_id: c.category_id,
                display_text: c.category_name,
              }))}
              onSelect={(obj) => handleFilterChange("categoryId", obj?.sale_id)}
              placeholder="Category"
              
            />
          </Col>
        );
      case "By Location":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Location</label>
            <SearchListAutoComplete
              entityType="location"
              optionsList={lookups.locations.map((l) => ({
                sale_id: l.location_id,
                display_text: l.location_name,
              }))}
              onSelect={(obj) => handleFilterChange("locationId", obj?.sale_id)}
              placeholder="Location"
              
            />
          </Col>
        );
      case "By User":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>User</label>
            <SearchListAutoComplete
              entityType="user"
              optionsList={lookups.users.map((u) => ({
                sale_id: u.id,
                display_text: u.full_name,
              }))}
              onSelect={(obj) => handleFilterChange("userId", obj?.sale_id)}
              placeholder="User"
              
            />
          </Col>
        );
      case "By Tag":
        return (
          <>
            <Col xs={24} sm={12} md={8} xl={5}>
              <label>Tag Group</label>
              <SearchListAutoComplete
                entityType="tag"
                optionsList={lookups.tagGroups?.map((tg) => ({
                  sale_id: tg.id,
                  display_text: tg.tag_groupname,
                }))}
                onSelect={(obj) => {
                  handleFilterChange("tag_groupnameid", obj?.sale_id);
                  if (obj?.sale_id) {
                    dispatch(fetchTagGroupFields(obj.sale_id));
                  }
                }}
                placeholder="Tag Group"
              />
            </Col>
            <Col xs={24} sm={12} md={8} xl={5}>
              <label>Tag Field</label>
              <SearchListAutoComplete
                entityType="tag"
                optionsList={lookups.tagFields?.map((f) => ({
                  sale_id: f.id,
                  display_text: f.display_text, // comes from thunk
                }))}
                onSelect={(obj) => handleFilterChange("tagFeildNameid", obj?.sale_id)}
                placeholder="Tag Field"
              />
            </Col>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Card className="product-replace-filters mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} wrap style={{ alignItems: "center" }}>
        {/* Filter Type */}
        <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4} >
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "All", display_text: "All" },
              { sale_id: "By Customer", display_text: "By Customer" },
              { sale_id: "By Item", display_text: "By Item" },
              { sale_id: "By Group", display_text: "By Group" },
              { sale_id: "By Category", display_text: "By Category" },
              { sale_id: "By Location", display_text: "By Location" },
              { sale_id: "By User", display_text: "By User" },
              { sale_id: "By Tag", display_text: "By Tag" },
            ]}
            value={{
              value: filters.filterType,
              label: filters.filterType,
            }}
            onSelect={(obj) => handleFilterChange("filterType", obj?.sale_id)}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {renderExtraField()}

        {/* Record Type */}
        <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4} >
          <label>Record Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "with item", display_text: "With Item" },
              { sale_id: "without item", display_text: "Without Item" },
            ]}
            value={
              filters.recordType
                ? { value: filters.recordType, label: filters.recordType === "with item"? "With Item" : "Without Item"}
                : undefined
            }
            onSelect={(obj) => handleFilterChange("recordType", obj?.sale_id)}
            placeholder="Record Type"
            showAddNew={false}
          />
        </Col>

        {/* Date Filter */}
        <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
          }}
        />

        {/* Report Button */}
        <Col
          xs={24} sm={12} md={9} xl={3}
          style={{ paddingTop: "1.4rem" }}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            style={{height: 35.19}}
            block
          >
            Report
          </Button>
        </Col>
      </Row>
      <style>{`
    @media (min-width: 768px) and (max-width: 1024px) {
      .product-replace-filters .ant-row .ant-col {
        flex: 0 0 50% !important;
        max-width: 50% !important;
      }
    }
  `}</style>
    </Card>
  );
};

export default QuotationRecordFilters;
